import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Percent } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const emptyForm = () => ({
  TaxID: null, TaxName: '',
  Tax1: '', Tax1Name: '', Tax2: '', Tax2Name: '',
  Tax3: '', Tax3Name: '', Tax4: '', Tax4Name: '',
  Tax5Amt: '', Tax5Name: '', IsActive: true,
});

const PRESETS = [
  { label: 'GST 5%',   data: { TaxName:'GST 5%',   Tax1:2.5, Tax1Name:'CGST', Tax2:2.5,  Tax2Name:'SGST' } },
  { label: 'GST 12%',  data: { TaxName:'GST 12%',  Tax1:6,   Tax1Name:'CGST', Tax2:6,    Tax2Name:'SGST' } },
  { label: 'GST 18%',  data: { TaxName:'GST 18%',  Tax1:9,   Tax1Name:'CGST', Tax2:9,    Tax2Name:'SGST' } },
  { label: 'GST 28%',  data: { TaxName:'GST 28%',  Tax1:14,  Tax1Name:'CGST', Tax2:14,   Tax2Name:'SGST' } },
  { label: 'IGST 5%',  data: { TaxName:'IGST 5%',  Tax1:5,   Tax1Name:'IGST' } },
  { label: 'IGST 12%', data: { TaxName:'IGST 12%', Tax1:12,  Tax1Name:'IGST' } },
  { label: 'IGST 18%', data: { TaxName:'IGST 18%', Tax1:18,  Tax1Name:'IGST' } },
  { label: 'IGST 28%', data: { TaxName:'IGST 28%', Tax1:28,  Tax1Name:'IGST' } },
];

const calcTotal = (f) =>
  [f.Tax1, f.Tax2, f.Tax3, f.Tax4].reduce((s, v) => s + (parseFloat(v) || 0), 0);

const TaxMaster = () => {
  const [taxes,        setTaxes]        = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal,    setShowModal]    = useState(false);
  const [editMode,     setEditMode]     = useState(false);
  const [formData,     setFormData]     = useState(emptyForm());
  const [errors,       setErrors]       = useState({});
  const [saving,       setSaving]       = useState(false);

  useEffect(() => { fetchTaxes(); }, []);

  useEffect(() => {
    let list = taxes;
    if (searchTerm.trim()) { const q = searchTerm.toLowerCase(); list = list.filter(t => t.TaxName.toLowerCase().includes(q)); }
    if (filterStatus !== 'all') list = list.filter(t => t.IsActive === (filterStatus === 'active' ? 1 : 0));
    setFiltered(list);
  }, [searchTerm, filterStatus, taxes]);

  const fetchTaxes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/TaxMaster.php');
      if (res.data.success) setTaxes(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const set = (field, value) => {
    setFormData(p => ({ ...p, [field]: value }));
    if (errors[field]) setErrors(p => { const n = { ...p }; delete n[field]; return n; });
  };

  const applyPreset = (preset) => {
    setFormData({ ...emptyForm(), ...preset.data, IsActive: true });
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!formData.TaxName.trim()) e.TaxName = 'Tax Name is required';
    if (formData.Tax1 === '' || formData.Tax1 === null) e.Tax1 = 'Tax 1 % is required';
    if (!formData.Tax1Name.trim()) e.Tax1Name = 'Tax 1 Name is required';
    [2,3,4].forEach(n => {
      const val  = formData[`Tax${n}`];
      const name = formData[`Tax${n}Name`];
      if ((val !== '' && val !== null) && !name.trim()) e[`Tax${n}Name`] = `Tax ${n} Name required`;
      if (name.trim() && (val === '' || val === null))   e[`Tax${n}`]     = `Tax ${n} % required`;
      if (val !== '' && val !== null && (parseFloat(val) < 0 || parseFloat(val) > 100)) e[`Tax${n}`] = 'Must be 0-100';
    });
    if (formData.Tax1 !== '' && (parseFloat(formData.Tax1) < 0 || parseFloat(formData.Tax1) > 100)) e.Tax1 = 'Must be 0-100';
    if (formData.Tax5Amt !== '' && parseFloat(formData.Tax5Amt) < 0) e.Tax5Amt = 'Cannot be negative';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...formData,
        Tax1: formData.Tax1 !== '' ? parseFloat(formData.Tax1) : null,
        Tax2: formData.Tax2 !== '' ? parseFloat(formData.Tax2) : null,
        Tax3: formData.Tax3 !== '' ? parseFloat(formData.Tax3) : null,
        Tax4: formData.Tax4 !== '' ? parseFloat(formData.Tax4) : null,
        Tax5Amt: formData.Tax5Amt !== '' ? parseFloat(formData.Tax5Amt) : 0,
        Tax1Name: formData.Tax1Name || null, Tax2Name: formData.Tax2Name || null,
        Tax3Name: formData.Tax3Name || null, Tax4Name: formData.Tax4Name || null,
        Tax5Name: formData.Tax5Name || null, IsActive: formData.IsActive ? 1 : 0,
      };
      const res = await apiClient[editMode ? 'put' : 'post']('/TaxMaster.php', payload);
      if (res.data.success) { closeModal(); fetchTaxes(); }
      else alert('Error: ' + res.data.message);
    } catch (err) { alert(err.response?.data?.message || 'Error saving tax'); }
    finally { setSaving(false); }
  };

  const handleEdit = (tax) => {
    setFormData({ TaxID: tax.TaxID, TaxName: tax.TaxName || '', Tax1: tax.Tax1 ?? '', Tax1Name: tax.Tax1Name || '', Tax2: tax.Tax2 ?? '', Tax2Name: tax.Tax2Name || '', Tax3: tax.Tax3 ?? '', Tax3Name: tax.Tax3Name || '', Tax4: tax.Tax4 ?? '', Tax4Name: tax.Tax4Name || '', Tax5Amt: tax.Tax5Amt ?? '', Tax5Name: tax.Tax5Name || '', IsActive: tax.IsActive === 1 });
    setEditMode(true); setErrors({}); setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this tax?')) return;
    try {
      const res = await apiClient.delete('/TaxMaster.php', { data: { TaxID: id } });
      if (res.data.success) fetchTaxes(); else alert(res.data.message);
    } catch (err) { alert(err.response?.data?.message || 'Error deleting tax'); }
  };

  const openAdd    = () => { setFormData(emptyForm()); setEditMode(false); setErrors({}); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setFormData(emptyForm()); setErrors({}); };

  const totalPer = calcTotal(formData);

  const inputCls = (err) =>
    `w-full px-3 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)]
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  const errMsg = (msg) => msg ? <p className="text-[11px] text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Percent size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Tax Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage GST / IGST / custom tax slabs</p>
          </div>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Tax
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search tax name…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]">
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          {filtered.length} {filtered.length === 1 ? 'tax' : 'taxes'}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading taxes…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Percent size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold">No taxes found</p>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Tax Name', 'Components', 'Total %', 'Fixed Amt (₹)', 'Status', 'Actions'].map((h, i) => (
                    <th key={h} className={`px-4 py-3.5 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0 ${i >= 2 && i <= 3 ? 'text-right' : i === 5 ? 'text-center' : 'text-left'}`}
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(tax => (
                  <tr key={tax.TaxID}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{tax.TaxName}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1.5">
                        {[1,2,3,4].map(n => {
                          const name = tax[`Tax${n}Name`];
                          const val  = tax[`Tax${n}`];
                          if (!name && (val === null || val === undefined)) return null;
                          return (
                            <span key={n} className="inline-block px-2.5 py-0.5 rounded-full text-[12px] font-bold bg-violet-100 text-violet-700">
                              {name} {parseFloat(val).toFixed(2)}%
                            </span>
                          );
                        })}
                        {tax.Tax5Name && parseFloat(tax.Tax5Amt) > 0 && (
                          <span className="inline-block px-2.5 py-0.5 rounded-full text-[12px] font-bold bg-amber-100 text-amber-700">
                            {tax.Tax5Name} ₹{parseFloat(tax.Tax5Amt).toFixed(2)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-right font-mono font-bold text-gray-800">
                      {parseFloat(tax.TotalPer || 0).toFixed(2)}%
                    </td>
                    <td className="px-4 py-3.5 text-right font-mono text-gray-500">
                      {parseFloat(tax.Tax5Amt || 0) > 0 ? `₹${parseFloat(tax.Tax5Amt).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        tax.IsActive ? 'bg-emerald-50 text-emerald-700 border-emerald-400' : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {tax.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex justify-center gap-1.5">
                        <button onClick={() => handleEdit(tax)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(tax.TaxID)} title="Deactivate"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[640px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <Percent size={18} /> {editMode ? 'Edit Tax' : 'Add New Tax'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* Quick presets */}
              {!editMode && (
                <div className="mb-5">
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-3">Quick Fill — Common Slabs</div>
                  <div className="flex flex-wrap gap-2">
                    {PRESETS.map(p => (
                      <button key={p.label} type="button" onClick={() => applyPreset(p)}
                        className="px-3.5 py-1.5 rounded-full text-[12px] font-bold cursor-pointer border-2 border-violet-400 bg-violet-100 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:border-violet-700">
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Tax Name */}
              <div className="mb-5">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Tax Name <span className="text-red-500">*</span>
                </label>
                <input type="text" value={formData.TaxName} onChange={e => set('TaxName', e.target.value)}
                  placeholder="e.g. GST 18%, IGST 5%" maxLength={100} className={inputCls(errors.TaxName)} />
                {errMsg(errors.TaxName)}
              </div>

              {/* Tax components 1-4 */}
              <div className="mb-5 px-4 py-4 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg"
                style={{ borderLeftColor: NAVY }}>
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">Tax Components (%)</div>

                {/* Header */}
                <div className="grid grid-cols-[90px_1fr_1fr] gap-3 mb-2 text-[11px] font-bold text-gray-400 uppercase tracking-wide">
                  <span></span><span>Component Name</span><span>Rate (%)</span>
                </div>

                {[1,2,3,4].map(n => (
                  <div key={n} className="grid grid-cols-[90px_1fr_1fr] gap-3 mb-3 items-start">
                    <div className="flex items-center pt-3 text-[13px] font-bold text-gray-600">
                      Tax {n}{n === 1 && <span className="text-red-500 ml-0.5">*</span>}
                    </div>
                    <div>
                      <input type="text" value={formData[`Tax${n}Name`]}
                        onChange={e => set(`Tax${n}Name`, e.target.value)}
                        placeholder={n === 1 ? 'e.g. CGST, IGST' : `Component ${n} name`} maxLength={50}
                        className={inputCls(errors[`Tax${n}Name`])} />
                      {errMsg(errors[`Tax${n}Name`])}
                    </div>
                    <div>
                      <input type="number" value={formData[`Tax${n}`]}
                        onChange={e => set(`Tax${n}`, e.target.value)}
                        placeholder="0.00" min={0} max={100} step="0.0001"
                        className={inputCls(errors[`Tax${n}`]) + ' text-right'} />
                      {errMsg(errors[`Tax${n}`])}
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div className="flex justify-end items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                  <span className="text-[13px] font-bold text-gray-600">Total Rate:</span>
                  <span className="text-[20px] font-extrabold text-violet-700 bg-violet-100 px-4 py-1 rounded-lg">
                    {totalPer.toFixed(2)}%
                  </span>
                </div>
              </div>

              {/* Fixed amount component */}
              <div className="mb-5 px-4 py-4 bg-amber-50 border border-amber-200 border-l-4 border-l-amber-400 rounded-r-lg">
                <div className="text-[11px] font-bold text-amber-700 uppercase tracking-widest mb-4">
                  Fixed Amount Component (₹) — optional
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Component Name</label>
                    <input type="text" value={formData.Tax5Name} onChange={e => set('Tax5Name', e.target.value)}
                      placeholder="e.g. Cess (flat), Surcharge" maxLength={50} className={inputCls(false)} />
                  </div>
                  <div>
                    <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Fixed Amount (₹)</label>
                    <input type="number" value={formData.Tax5Amt} onChange={e => set('Tax5Amt', e.target.value)}
                      placeholder="0.00" min={0} step="0.01" className={inputCls(errors.Tax5Amt) + ' text-right'} />
                    {errMsg(errors.Tax5Amt)}
                  </div>
                </div>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input type="checkbox" checked={formData.IsActive} onChange={e => set('IsActive', e.target.checked)}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={saving}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {saving ? 'Saving…' : editMode ? 'Update Tax' : 'Create Tax'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default TaxMaster;